import { useState } from "react";
import styles from "./EonStreamingView.module.css";
import { SignalingStatus } from "../constants/webrtc";
import type { StreamStatus } from "..";

interface ConnectionStatusPanelProps {
  videoSignalingStatus: SignalingStatus;
  audioSignalingStatus: SignalingStatus;
  videoWebRTCConnected: boolean;
  videoWebRTCState: string;
  audioWebRTCConnected: boolean;
  audioWebRTCState: string;
  isDataChannelOpen: boolean;
  streamStatus?: StreamStatus;
  onReconnect?: () => void;
  onSimulateDisconnectReconnect?: () => void;
}

export const ConnectionStatusPanel = ({
  videoSignalingStatus,
  audioSignalingStatus,
  videoWebRTCConnected,
  videoWebRTCState,
  audioWebRTCConnected,
  audioWebRTCState,
  isDataChannelOpen,
  streamStatus,
  onReconnect,
  onSimulateDisconnectReconnect,
}: ConnectionStatusPanelProps) => {
  const [isControlsOpen, setIsControlsOpen] = useState(false);

  const isVideoSignalingConnecting =
    videoSignalingStatus === SignalingStatus.CONNECTING;
  const isVideoSignalingConnected =
    videoSignalingStatus === SignalingStatus.CONNECTED;

  const isAudioSignalingConnecting =
    audioSignalingStatus === SignalingStatus.CONNECTING;
  const isAudioSignalingConnected =
    audioSignalingStatus === SignalingStatus.CONNECTED;

  return (
    <div className={styles.controls}>
      <button
        className={styles.controlsToggle}
        onClick={() => setIsControlsOpen(!isControlsOpen)}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "rgba(0, 0, 0, 0.7)",
          color: "white",
          border: "none",
          padding: "8px 12px",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "12px",
          zIndex: 1000,
        }}
      >
        {isControlsOpen ? "Hide Status" : "Show Status"}
      </button>

      {isControlsOpen && (
        <div
          className={styles.statusPanel}
          style={{
            position: "absolute",
            top: "50px",
            right: "10px",
            background: "rgba(0, 0, 0, 0.8)",
            padding: "12px",
            borderRadius: "8px",
            maxWidth: "300px",
            fontSize: "12px",
            zIndex: 999,
          }}
        >
          <div
            className={`${styles.status} ${
              isVideoSignalingConnecting
                ? styles.statusConnecting
                : isVideoSignalingConnected
                ? styles.statusConnected
                : styles.statusDisconnected
            }`}
            style={{ marginBottom: "8px" }}
          >
            Video Signaling:{" "}
            {isVideoSignalingConnecting
              ? "Connecting"
              : isVideoSignalingConnected
              ? "Connected"
              : "Disconnected"}
          </div>

          <div
            className={`${styles.status} ${
              isAudioSignalingConnecting
                ? styles.statusConnecting
                : isAudioSignalingConnected
                ? styles.statusConnected
                : styles.statusDisconnected
            }`}
            style={{ marginBottom: "8px" }}
          >
            Audio Signaling:{" "}
            {isAudioSignalingConnecting
              ? "Connecting"
              : isAudioSignalingConnected
              ? "Connected"
              : "Disconnected"}
          </div>

          <div
            className={`${styles.status} ${
              videoWebRTCConnected
                ? styles.statusConnected
                : styles.statusDisconnected
            }`}
            style={{ marginBottom: "8px" }}
          >
            Video WebRTC: {videoWebRTCState}
          </div>

          <div
            className={`${styles.status} ${
              audioWebRTCConnected
                ? styles.statusConnected
                : styles.statusDisconnected
            }`}
            style={{ marginBottom: "8px" }}
          >
            Audio WebRTC: {audioWebRTCState}
          </div>

          <div
            className={`${styles.status} ${
              isDataChannelOpen
                ? styles.statusConnected
                : styles.statusDisconnected
            }`}
            style={{ marginBottom: "8px" }}
          >
            Data Channel: {isDataChannelOpen ? "Open" : "Closed"}
          </div>
          <button onClick={onReconnect} className={styles.disconnectButton}>
            Re-connect
          </button>
          <button
            onClick={onSimulateDisconnectReconnect}
            className={styles.disconnectButton}
            style={{ marginTop: "4px", background: "#b45309" }}
          >
            Simulate Disconnect/Reconnect
          </button>
          <div className={styles.status}>
            Stream Status: {streamStatus?.status || "unknown"}
            {streamStatus?.message && (
              <div style={{ fontSize: "11px", marginTop: "4px", opacity: 0.8 }}>
                {streamStatus.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
